import { Injectable } from "@angular/core";
import { AccountModuleBreezeModel } from "@common/ADAPT.Common.Model/account/account-module";
import { Board, BoardBreezeModel } from "@common/ADAPT.Common.Model/organisation/board";
import { ConnectionBreezeModel } from "@common/ADAPT.Common.Model/organisation/connection";
import { ConnectionType } from "@common/ADAPT.Common.Model/organisation/connection-type";
import { CulturalActivityBreezeModel } from "@common/ADAPT.Common.Model/organisation/cultural-activity";
import { CulturalActivitySupplementaryDataBreezeModel } from "@common/ADAPT.Common.Model/organisation/cultural-activity-supplementary-data";
import { CulturalActivityType } from "@common/ADAPT.Common.Model/organisation/cultural-activity-type";
import { CulturalRelationshipBreezeModel } from "@common/ADAPT.Common.Model/organisation/cultural-relationship";
import { DiagramBreezeModel } from "@common/ADAPT.Common.Model/organisation/diagram";
import { FeatureStatusBreezeModel } from "@common/ADAPT.Common.Model/organisation/feature-status";
import { GuidingPhilosophyBreezeModel } from "@common/ADAPT.Common.Model/organisation/guiding-philosophy";
import { ItemBreezeModel } from "@common/ADAPT.Common.Model/organisation/item";
import { ItemCommentBreezeModel } from "@common/ADAPT.Common.Model/organisation/item-comment";
import { KeyResultBreezeModel } from "@common/ADAPT.Common.Model/organisation/key-result";
import { KeyResultValueBreezeModel } from "@common/ADAPT.Common.Model/organisation/key-result-value";
import { LabelBreezeModel } from "@common/ADAPT.Common.Model/organisation/label";
import { LabelLocationBreezeModel } from "@common/ADAPT.Common.Model/organisation/label-location";
import { LayoutBreezeModel } from "@common/ADAPT.Common.Model/organisation/layout";
import { LinkBreezeModel } from "@common/ADAPT.Common.Model/organisation/link";
import { MeetingBreezeModel } from "@common/ADAPT.Common.Model/organisation/meeting";
import { MeetingAgendaItemBreezeModel } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-item";
import { MeetingAgendaItemSupplementaryDataBreezeModel } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-item-supplementary-data";
import { MeetingAttendeeBreezeModel } from "@common/ADAPT.Common.Model/organisation/meeting-attendee";
import { MeetingNoteBreezeModel } from "@common/ADAPT.Common.Model/organisation/meeting-note";
import { MeetingSupplementaryDataBreezeModel } from "@common/ADAPT.Common.Model/organisation/meeting-supplementary-data";
import { ObjectiveBreezeModel } from "@common/ADAPT.Common.Model/organisation/objective";
import { ObjectiveCommentBreezeModel } from "@common/ADAPT.Common.Model/organisation/objective-comment";
import { ObjectiveReviewBreezeModel } from "@common/ADAPT.Common.Model/organisation/objective-review";
import { OrganisationDetailBreezeModel } from "@common/ADAPT.Common.Model/organisation/organisation-detail";
import { OutstandingSurveyResponseBreezeModel } from "@common/ADAPT.Common.Model/organisation/outstanding-survey-response";
import { ProcessMapBreezeModel } from "@common/ADAPT.Common.Model/organisation/process-map";
import { ProcessStepBreezeModel } from "@common/ADAPT.Common.Model/organisation/process-step";
import { ProcessStepGuidanceMaterialBreezeModel } from "@common/ADAPT.Common.Model/organisation/process-step-guidance-material";
import { ProcessStepSupplementaryDataBreezeModel } from "@common/ADAPT.Common.Model/organisation/process-step-supplementary-data";
import { ProgressCategoryBreezeModel } from "@common/ADAPT.Common.Model/organisation/progress-category";
import { ProgressItemBreezeModel } from "@common/ADAPT.Common.Model/organisation/progress-item";
import { ProgressProductBreezeModel } from "@common/ADAPT.Common.Model/organisation/progress-product";
import { PurposeBreezeModel } from "@common/ADAPT.Common.Model/organisation/purpose";
import { ResilientBusinessSurveyConfigurationBreezeModel } from "@common/ADAPT.Common.Model/organisation/resilient-business-survey-configuration";
import { RoleBreezeModel } from "@common/ADAPT.Common.Model/organisation/role";
import { RoleConnectionBreezeModel } from "@common/ADAPT.Common.Model/organisation/role-connection";
import { RoleFeaturePermissionBreezeModel } from "@common/ADAPT.Common.Model/organisation/role-feature-permission";
import { RoleLocationBreezeModel } from "@common/ADAPT.Common.Model/organisation/role-location";
import { RoleSupplementaryDataBreezeModel } from "@common/ADAPT.Common.Model/organisation/role-supplementary-data";
import { SurveyBreezeModel } from "@common/ADAPT.Common.Model/organisation/survey";
import { SystemComponentBreezeModel } from "@common/ADAPT.Common.Model/organisation/system-component";
import { SystemComponentSupplementaryDataBreezeModel } from "@common/ADAPT.Common.Model/organisation/system-component-supplementary-data";
import { SystemDocumentBreezeModel } from "@common/ADAPT.Common.Model/organisation/system-document";
import { SystemEntityBreezeModel } from "@common/ADAPT.Common.Model/organisation/system-entity";
import { SystemKnowledgeExpertBreezeModel } from "@common/ADAPT.Common.Model/organisation/system-knowledge-expert";
import { SystemLocationBreezeModel } from "@common/ADAPT.Common.Model/organisation/system-location";
import { TeamBreezeModel } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamLocationBreezeModel } from "@common/ADAPT.Common.Model/organisation/team-location";
import { ValueBreezeModel } from "@common/ADAPT.Common.Model/organisation/value";
import { ValueQuestionBreezeModel } from "@common/ADAPT.Common.Model/organisation/value-question";
import { ValuesConstitutionBreezeModel } from "@common/ADAPT.Common.Model/organisation/values-constitution";
import { VisionBreezeModel } from "@common/ADAPT.Common.Model/organisation/vision";
import { WatcherLinkBreezeModel } from "@common/ADAPT.Common.Model/organisation/watcher-link";
import { WorkflowConnectionBreezeModel } from "@common/ADAPT.Common.Model/organisation/workflow-connection";
import { WorkflowStatusBreezeModel } from "@common/ADAPT.Common.Model/organisation/workflow-status";
import { PersonContact, PersonContactBreezeModel } from "@common/ADAPT.Common.Model/person/person-contact";
import { PersonDetailBreezeModel } from "@common/ADAPT.Common.Model/person/person-detail";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { DateFormats } from "@common/ux/date-formats";
import moment from "moment";
import { forkJoin, Observable, of, switchMap } from "rxjs";
import { IntegratedArchitectureFrameworkQueryUtilities } from "../architecture/integrated-architecture-framework-query-utilities";
import { FeaturesService } from "../features/features.service";
import { CommonTeamsService } from "../teams/common-teams.service";
import { EntityAutoUpdaterTemplateBuilder } from "./entity-auto-updater-template-builder";
import { EntitySignalRHub } from "./entity-signalr-hub.service";

@Injectable({
    providedIn: "root",
})
export class EntitySignalRRegistrarService {
    public constructor(private commonDataService: CommonDataService) { }

    public registerUpdaterTemplates() {
        this.registerOrganisationTemplates();
        this.registerRoleFeatureTemplates();
        this.registerSystemisationTemplates();
        this.registerValuesConstitutionTemplates();
        this.registerGuidingPhilosophyTemplates();
        this.registerObjectiveTemplates();
        this.registerKanbanTemplates();
        this.registerMeetingTemplates();
        this.registerLocationTemplates();
        this.registerTeamTemplates();
        this.registerAccountModuleTemplate();
        this.registerSurveyTemplates();
        this.registerWorkflowEntities();
        this.registerLabelEntities();
        this.registerLinkEntities();
        this.registerWatcherLinkEntities();
        this.registerCulturalRelationshipEntities();
        this.registerProgressProductEntities();
        this.registerVisionEntities();
    }

    private registerOrganisationTemplates() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(EntityAutoUpdaterTemplateBuilder.forModel(OrganisationDetailBreezeModel)
            .withSummaryHtml(() => `Organisation detail`)
            .fetchAddedEntityIfRelatedEntityInCache()
            .build());
    }

    private registerProgressProductEntities() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(ProgressItemBreezeModel)
                .withSummaryHtml((i) => `Progress item <strong>${i.name}</strong>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(ProgressCategoryBreezeModel)
                .withSummaryHtml((i) => `Progress category <strong>${i.name}</strong>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(ProgressProductBreezeModel)
                .withSummaryHtml((i) => `Progress product <strong>${i.name}</strong>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
    }

    private registerVisionEntities() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(VisionBreezeModel)
                .withSummaryHtml(() => `Vision statement`)
                // The only time we want to fetch this is when it is already in the cache
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
    }

    private registerCulturalRelationshipEntities() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(CulturalRelationshipBreezeModel)
                .withSummaryHtml((i) => `Cultural relationship between ${i.culturalLeader?.fullName} and ${i.person?.fullName}`)
                // have to fetch this or relationship added from another session will never show up if the current session did a getAll before (e.g. CLF page)
                .fetchingAddedEntityWhen(() => true)
                .build());

        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(CulturalActivityBreezeModel)
                .withSummaryHtml((i) => `Cultural event of type ${CulturalActivityType[i.type]}`)
                // have to fetch this or relationship added from another session will never show up if the current session did a getAll before (e.g. CLF page)
                .fetchingAddedEntityWhen(() => true)
                .build());

        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(CulturalActivitySupplementaryDataBreezeModel)
                .withSummaryHtml((i) => `Supplementary data for cultural event of type ${CulturalActivityType[i.culturalActivity.type]}`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
    }

    private registerLabelEntities() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(LabelBreezeModel)
                .withSummaryHtml((i) => `<strong>${i.name}</strong>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(LabelLocationBreezeModel)
                .withSummaryHtml((i) => `Label location for label: <i>"${i.label?.name}"</i>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
    }

    private registerLinkEntities() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(LinkBreezeModel)
                .withSummaryHtml((e) => {

                    if (e.extensions.isItemToItemLink) {
                        return `Link between <strong>${e.item?.code}</strong> and <strong>${e.linkedItem?.code}</strong>`;
                    }
                    if (e.extensions.isObjectiveToItemLink) {
                        return `Link between <strong>${e.item?.code}</strong> and <strong>${e.objective1?.objectiveCode}</strong>`;
                    }
                    if (e.extensions.isObjectiveToObjectiveLink) {
                        return `Link between <strong>${e.objective1?.objectiveCode}</strong> and <strong>${e.objective2?.objectiveCode}</strong>`;
                    }
                    if (e.extensions.isItemToMeetingLink) {
                        return `Link between <strong>${e.item?.code}</strong> and <strong>${e.meeting?.name}</strong> and <strong>${e.meetingAgendaItem?.name}</strong>`;
                    }
                    return "Link updated";
                })
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
    }

    private registerWatcherLinkEntities() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(WatcherLinkBreezeModel)
                .withSummaryHtml((i) => `Watcher link for ${i.item?.code ?? i.objective?.objectiveCode ?? i.watcherLinkId}`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(PersonDetailBreezeModel)
                .withSummaryHtml((i) => `Person detail for person ${i.person?.fullName}`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
    }

    private registerWorkflowEntities() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(EntityAutoUpdaterTemplateBuilder.forModel(WorkflowConnectionBreezeModel)
            .withSummaryHtml((i) => `Workflow connection for workflow <strong>${i.workflowId}</strong> to teamId ${i.teamId}, connectionId ${i.connectionId}`)
            .fetchingAddedEntityWhen(() => true)
            .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(EntityAutoUpdaterTemplateBuilder.forModel(WorkflowStatusBreezeModel)
            .withSummaryHtml((i) => `Workflow status ${i.status}, workflowId ${i.workflowId}, workflowStatusId ${i.workflowStatusId}, connection: ${i.workflowConnectionId}:${i.workflowConnection?.workflowId}`)
            .fetchAddedEntityIfRelatedEntityInCache()
            .build());
    }

    private registerSurveyTemplates() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(EntityAutoUpdaterTemplateBuilder.forModel(SurveyBreezeModel)
            .withSummaryHtml((i) => `Survey <strong>${i.name}</strong>`)
            .fetchingAddedEntityWhen(() => true) // always fetch new team
            .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(EntityAutoUpdaterTemplateBuilder.forModel(OutstandingSurveyResponseBreezeModel)
            .withSummaryHtml((i) => `Outstanding survey response for survey <strong>${i.survey.name}</strong>`)
            .fetchAddedEntityIfRelatedEntityInCache()
            .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(EntityAutoUpdaterTemplateBuilder.forModel(ResilientBusinessSurveyConfigurationBreezeModel)
            .withSummaryHtml((i) => `RB survey configuration for organisation <strong>${i.organisationId}</strong> updated`)
            .fetchAddedEntityIfRelatedEntityInCache()
            .build());
    }

    private registerTeamTemplates() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(EntityAutoUpdaterTemplateBuilder.forModel(TeamBreezeModel)
            .withSummaryHtml((i) => `Team <strong>${i.name}</strong>`)
            .fetchingAddedEntityWhen(() => true) // always fetch new team
            .primeAfterFetch((team) => {
                // need this for newly created teams
                const featuresService = this.commonDataService.injector.get(FeaturesService);
                const teamsService = this.commonDataService.injector.get(CommonTeamsService);
                const archData = new IntegratedArchitectureFrameworkQueryUtilities(this.commonDataService);
                return forkJoin([
                    archData.primeRolesForTeamId(team.teamId),
                    this.commonDataService.getByPredicate(BoardBreezeModel, new MethodologyPredicate<Board>("teamId", "==", team.teamId), true),
                    teamsService.getTeamLocations(team, true),
                ]).pipe(
                    // make this the last prime query as primeTeam in common-teams.service.ts called after creating owner and leaders team
                    // is going to wait for this entity type to be available to determine if the team is fully primed
                    switchMap(() => featuresService.updateFeatureStatusesForTeam(team.teamId)),
                );
            })
            .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(TeamLocationBreezeModel)
                .withSummaryHtml((i) => i.team
                    ? `Location for team <strong>${i.team.name}</strong>`
                    : "Location for unavailable team")
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(EntityAutoUpdaterTemplateBuilder.forModel(FeatureStatusBreezeModel)
            .withSummaryHtml((i) => `Feature ${i.feature?.name} for team ${i.team?.name}`)
            .fetchingAddedEntityWhen(() => true) // when creating new team, feature status, team and roles pushed through in a single array
            // - so cannot fetchingAddIfRelatedEntityInCache the same as team Role (which is also fetch-always)
            .build());
    }

    private registerAccountModuleTemplate() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(EntityAutoUpdaterTemplateBuilder.forModel(AccountModuleBreezeModel)
            .withSummaryHtml((i) => `AccountModule ${i.module?.name}`)
            .fetchAddedEntityIfRelatedEntityInCache() // won't fetch if module for another org account changes
            .build());
    }

    private registerRoleFeatureTemplates() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(RoleBreezeModel)
                .withSummaryHtml((i) => `<strong>${i.label}</strong>`)
                .fetchingAddedEntityWhen(() => true)
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(RoleSupplementaryDataBreezeModel)
                .withSummaryHtml((i) => `Supplementary data for role <i>${i.role.label}</i>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(RoleConnectionBreezeModel)
                .withSummaryHtml((i) => `Connection data for role <i>${i.role.label}</i>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .primeAfterFetch((rc) => {
                    const primeObservables: Observable<unknown>[] = [];
                    if (rc.teamId && !rc.team) {
                        primeObservables.push(this.commonDataService.getById(TeamBreezeModel, rc.teamId!));
                    }

                    if (rc.connection?.connectionType === ConnectionType.Employee) {
                        // prime position through navproperty of connection
                        if (!rc.connection.positions || rc.connection.positions.length < 1) {
                            primeObservables.push(this.commonDataService.getById(ConnectionBreezeModel, rc.connectionId, true));
                        }
                    }

                    if (rc.connection?.personId && (!rc.connection?.person?.personContacts || rc.connection.person.personContacts.length < 1)) {
                        // prime person contacts - need force remote or otherwise it will just access cache and from previous getAll call
                        primeObservables.push(this.commonDataService.getByPredicate(PersonContactBreezeModel, new MethodologyPredicate<PersonContact>("personId", "==", rc.connection.personId), true));
                    }

                    if (primeObservables.length > 0) {
                        return forkJoin(primeObservables);
                    } else {
                        return of(undefined);
                    }
                })
                // user can't do anything about these changes, auto-integrate them
                .conflictsShouldBeAutomaticallyIntegrated(true)
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(RoleFeaturePermissionBreezeModel)
                .withSummaryHtml((i) => `Feature permission <i>${i.featurePermission.name}</i> for role <i>${i.role.label}</i>`)
                // no endpoint to update this data specifically
                .fetchingAddedEntityWhen(() => false)
                // user can't do anything about these changes, auto-integrate them
                .conflictsShouldBeAutomaticallyIntegrated(true)
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(ConnectionBreezeModel)
                .withSummaryHtml((i) => `Connection for person <i>${i.person?.fullName}</i>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
    }

    private registerSystemisationTemplates() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(EntityAutoUpdaterTemplateBuilder.forModel(SystemEntityBreezeModel)
            .withSummaryHtml((i) => `System <strong>${i.name}</strong>`)
            .fetchingAddedEntityWhen(() => true)
            .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(EntityAutoUpdaterTemplateBuilder.forModel(SystemKnowledgeExpertBreezeModel)
            .withSummaryHtml((i) => `Knowledge expert <strong>${i.knowledgeExpert.fullName}</strong> for system <strong>${i.system.name}</strong>`)
            .fetchAddedEntityIfRelatedEntityInCache()
            .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(EntityAutoUpdaterTemplateBuilder.forModel(SystemComponentBreezeModel)
            .withSummaryHtml((i) => `<strong>${i.name}</strong> element in system <strong>${i.system.name}</strong>`)
            .fetchAddedEntityIfRelatedEntityInCache()
            .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(EntityAutoUpdaterTemplateBuilder.forModel(SystemComponentSupplementaryDataBreezeModel)
            .withSummaryHtml((i) => `Supplementary data for system element <strong>${i.systemComponent.name}</strong>`)
            .fetchAddedEntityIfRelatedEntityInCache()
            .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(EntityAutoUpdaterTemplateBuilder.forModel(DiagramBreezeModel)
            .withSummaryHtml(() => `System diagram entity`)
            .fetchAddedEntityIfRelatedEntityInCache()
            .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(EntityAutoUpdaterTemplateBuilder.forModel(SystemDocumentBreezeModel)
            .withSummaryHtml(() => `System document entity`)
            .fetchAddedEntityIfRelatedEntityInCache()
            .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(EntityAutoUpdaterTemplateBuilder.forModel(LayoutBreezeModel)
            .withSummaryHtml(() => `Layout entity`)
            .fetchingAddedEntityWhen(() => true)
            .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(EntityAutoUpdaterTemplateBuilder.forModel(SystemLocationBreezeModel)
            .withSummaryHtml((i) => `system location data for system <strong>${i.system.name}</strong>`)
            .fetchAddedEntityIfRelatedEntityInCache()
            .build());

        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(ProcessMapBreezeModel)
                .withSummaryHtml((i) => `<strong>${i.name}</strong>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(ProcessStepBreezeModel)
                .withSummaryHtml((i) => `<strong>${i.name}</strong>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(ProcessStepSupplementaryDataBreezeModel)
                .withSummaryHtml((i) => `Supplementary data for process step <strong>${i.processStep?.name}</strong>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(ProcessStepGuidanceMaterialBreezeModel)
                .withSummaryHtml((i) => `Guidance material <i>${i.guidanceMaterial?.name}</i> for process step <strong>${i.processStep?.name}</strong>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
    }

    private registerValuesConstitutionTemplates() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(ValuesConstitutionBreezeModel)
                .withSummaryHtml((i) => {
                    const target = i.teamId
                        ? `${i.team.name} team`
                        : "Organisation";
                    return `${target} values`;
                })
                .fetchAddedEntityIfAnyExistingOptionalTeamEntityInCache()
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(ValueBreezeModel)
                .withSummaryHtml((i) => {
                    const target = i.valuesConstitution.teamId
                        ? `${i.valuesConstitution.team.name} team`
                        : "Organisation";
                    return `${target} value: ${i.name}`;
                })
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(ValueQuestionBreezeModel)
                .withSummaryHtml((i) => `Question for ${i.value.name}: ${i.question}`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
    }

    private registerGuidingPhilosophyTemplates() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(GuidingPhilosophyBreezeModel)
                .withSummaryHtml(() => `Organisation purpose and vision`)
                // The only time we want to fetch this is when it is already in the cache
                .fetchingAddedEntityWhen(() => false)
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(PurposeBreezeModel)
                .withSummaryHtml((i) => {
                    const target = i.teamId
                        ? `${i.team.name} team`
                        : "Organisation";
                    return `${target} purpose`;
                })
                .fetchAddedEntityIfAnyExistingOptionalTeamEntityInCache()
                .build());
    }

    private registerObjectiveTemplates() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(ObjectiveBreezeModel)
                .withSummaryHtml((o) => `<strong>${o.objectiveCode}</strong> ${o.title}`)
                .fetchAddedEntityIfAnyExistingOptionalTeamEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(ObjectiveReviewBreezeModel)
                .withSummaryHtml((r) => `Review for ${r.objective?.objectiveCode}`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(ObjectiveCommentBreezeModel)
                .withSummaryHtml((c) => `Comment on ${c.objective.title}: <i>"${c.comment}"</i>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(KeyResultBreezeModel)
                .withSummaryHtml((kr) => `<strong>${kr.title}</strong>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(KeyResultValueBreezeModel)
                .withSummaryHtml((krv) => `Key Result progress update on ${moment(krv.dateTime).format(DateFormats.moment.short)}: ${krv.formattedValue}`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
    }

    private registerKanbanTemplates() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(BoardBreezeModel)
                .withSummaryHtml((e) => `Board ${e.name}`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(ItemBreezeModel)
                .withSummaryHtml((i) => i.extensions.getSummaryHtml())
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(ItemCommentBreezeModel)
                .withSummaryHtml((e) => `Comment on <strong>${e.item.code}</strong>: <i>"${e.comment}"</i>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
    }

    private registerMeetingTemplates() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(MeetingBreezeModel)
                .withSummaryHtml((i) => `<strong>${i.name}</strong>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(MeetingAttendeeBreezeModel)
                .withSummaryHtml((i) => `Meeting <i>${i.meeting.name}</i>: Attendee <i>${i.attendee.fullName}</i>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(MeetingAgendaItemBreezeModel)
                .withSummaryHtml((i) => `Meeting agenda item <i>${i.name}</i>`)
                .fetchAddedEntityIfRelatedEntityInCache()
                // related entity for suppData is meeting agenda item - if they are saved in a single saveChanges POST,
                // suppData entity may be synced first and since related entity not in cache, it won't be fetched.
                // So, just fetch it here!
                .primeAfterFetch((rc) => this.commonDataService.getById(MeetingAgendaItemSupplementaryDataBreezeModel, rc.meetingAgendaItemId))
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(MeetingAgendaItemSupplementaryDataBreezeModel)
                // CM-5316: meetingAgendaItem was null
                // (brought up conflicting changes dialog, not sure how you can have conflicting changes if you don't have the entity...)
                .withSummaryHtml((i) => "Supplementary data for " + (i.meetingAgendaItem ? `agenda item <i>${i.meetingAgendaItem.name}</i>` : "unknown agenda item"))
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(MeetingNoteBreezeModel)
                .withSummaryHtml((i) => `Meeting ${i.type}: ${i.content}`)
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(MeetingSupplementaryDataBreezeModel)
                .withSummaryHtml((i) => "Supplementary data for " + (i.meeting ? `meeting <i>${i.meeting.name}</i>` : "unknown meeting"))
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
    }

    private registerLocationTemplates() {
        EntitySignalRHub.addEntityAutoUpdaterTemplate(
            EntityAutoUpdaterTemplateBuilder.forModel(RoleLocationBreezeModel)
                .withSummaryHtml((i) => i.role
                    ? `Location for role <strong>${i.role.label}</strong>`
                    : "Location for unavailable role")
                .fetchAddedEntityIfRelatedEntityInCache()
                .build());
    }
}
